import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Brush,
  AutoFixNormal,
  Remove,
  CropSquare,
  RadioButtonUnchecked,
  Undo,
  Delete,
  FiberManualRecord,
  GridOn,
  ViewAgenda,
  MoreHoriz,
  Crop as CropIcon,
  Title as TextIcon,
  Image as ImageIcon,
  VisibilityOff,
  Visibility,
  PermMedia,
  Replay,
  AddPhotoAlternate as UploadPhotoIcon,
} from '@mui/icons-material';
import AreaCaptureOverlay from './AreaCaptureOverlay';

// ---- Tool constants ----
const TOOL = {
  PEN: 'pen',
  ERASER: 'eraser',
  LINE: 'line',
  RECT: 'rect',
  CIRCLE: 'circle',
  TEXT: 'text',
};

const STROKE_WIDTHS = { S: 2, M: 4, L: 8 };
const ERASER_WIDTHS = { S: 10, M: 20, L: 36 };
const COLOR_PRESETS = ['#000000', '#1565c0', '#c62828'];
const FLOAT_SCALE_MIN = 25;
const FLOAT_SCALE_MAX = 400;
const FLOAT_MIN_SIZE = 60;

/**
 * DrawingAnswerPad
 *
 * A full drawing canvas with a toolbox for students to write worked answers,
 * sketch diagrams, and show mathematical working.
 *
 * Props:
 *   questionIdx             — resets canvas when question changes
 *   onChange                — called with base64 PNG data URL (or { dataUrl, diagramOnCanvas } object) whenever the drawing changes
 *   minHeight               — minimum canvas height in px (default 350)
 *   label                   — optional label shown in toolbar (e.g. "Your Working")
 *   initialDrawing          — base64 PNG data URL to restore on mount / question change (e.g. saved answer)
 *   bgMode                  — 'lines' | 'grid' | 'dots' for background guide (default 'lines')
 *   marks                   — question marks; minHeight scales as marks * 120 (e.g. 4 marks = 480px)
 *   autoInsertFloating      — when true, floating overlays are committed to canvas immediately (no manual Insert needed)
 *   autoLoadQuestionDiagram — when true, the primary question image is loaded onto the canvas on each new question
 *                             (only when there is no existing answer). onChange receives { dataUrl, diagramOnCanvas: true }
 *                             so callers can skip verify-time compositeWithContext.
 */
export default function DrawingAnswerPad({ questionIdx, onChange, minHeight: minHeightProp, label, initialDrawing, bgMode = 'lines', marks, contextImageUrl, pendingFloatImage, onFloatImageConsumed, questionImages, autoInsertFloating = false, autoLoadQuestionDiagram = false }) {
  const computedMinHeight = marks != null ? Math.max(200, Math.min(400, marks * 80)) : undefined;
  const minHeight = computedMinHeight ?? minHeightProp ?? 350;
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef(null);
  const snapshotRef = useRef(null); // ImageData snapshot before current stroke (for shape preview)
  const undoSnapshotRef = useRef(null); // data-URL snapshot before current stroke (for undo history)
  const userStrokesRef = useRef(false); // true once user has drawn at least one stroke
  const ownEmitRef = useRef(false); // true when initialDrawing change came from our own emitChange — skip re-init
  const diagramOnCanvasRef = useRef(false); // true when autoLoadQuestionDiagram baked diagram onto canvas (skip compositeWithContext at verify time)
  const autoLoadedForIdxRef = useRef(null); // tracks which questionIdx had its diagram auto-loaded (prevents duplicate loads)

  const [activeTool, setActiveTool] = useState(TOOL.PEN);
  const [drawColor, setDrawColor] = useState('#000000');
  const [strokeSize, setStrokeSize] = useState('M');
  const [history, setHistory] = useState([]); // array of data-URL strings for undo (dimension-independent)
  const [canUndo, setCanUndo] = useState(false);
  const [canvasHeight, setCanvasHeight] = useState(minHeight);
  const [bgModeState, setBgModeState] = useState(bgMode || 'lines');
  const [captureMode, setCaptureMode] = useState(false);
  const [showDiagram, setShowDiagram] = useState(false);
  const [textCursor, setTextCursor] = useState(null); // { x, y, text } — active text input overlay
  const [fontSize, setFontSize] = useState(20);
  // floatingImage: captured screenshot placed as resizable/draggable overlay before committing to canvas
  const [floatingImage, setFloatingImage] = useState(null); // { src, x, y, w, h, baseW, baseH }
  const [dragOver, setDragOver] = useState(false);
  const [imgMenuAnchor, setImgMenuAnchor] = useState(null);
  const [lastCapturedSrc, setLastCapturedSrc] = useState(null);

  // ---- Reset when question changes; restore initialDrawing if provided ----
  // Skip re-initialization when the initialDrawing change came from our own emitChange
  // (otherwise every stroke would wipe the undo history via setHistory([])).
  useEffect(() => {
    if (ownEmitRef.current) {
      ownEmitRef.current = false;
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Reset diagram-on-canvas flag for the new question
    diagramOnCanvasRef.current = false;

    if (initialDrawing) {
      userStrokesRef.current = true; // restoring a saved answer counts as having content
      const img = new Image();
      img.onload = () => {
        // Reset transform to draw at physical pixel coordinates, then restore DPR scale
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        setHistory([]);
        setCanUndo(false);
      };
      img.onerror = () => {
        userStrokesRef.current = false;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        setHistory([]);
        setCanUndo(false);
        if (onChange) onChange(null);
      };
      img.src = initialDrawing;
    } else {
      userStrokesRef.current = false;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      setHistory([]);
      setCanUndo(false);
      if (onChange) onChange(null);
    }
  }, [questionIdx, initialDrawing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Auto-load question diagram onto canvas (diagram/plot questions) ----
  // Fires when autoLoadQuestionDiagram=true, question changes, and there's no existing answer.
  // The diagram is drawn directly to the canvas (no floating overlay) so the student
  // can draw on top immediately. emitChange is NOT called here — a bare auto-loaded
  // diagram is not an answer; emitChange fires only once the student draws a stroke.
  useEffect(() => {
    if (!autoLoadQuestionDiagram) return;
    if (initialDrawing) return; // existing answer restored — don't overwrite
    if (autoLoadedForIdxRef.current === questionIdx) return; // already loaded for this question
    autoLoadedForIdxRef.current = questionIdx;

    const primaryUrl =
      (Array.isArray(questionImages) && questionImages.length > 0 ? questionImages[0].url : null) ||
      contextImageUrl;
    if (!primaryUrl) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');

    const place = (safeDataUrl) => {
      const img = new Image();
      img.onload = () => {
        const cssW = Math.round(canvas.width / dpr);
        const cssH = Math.round(canvas.height / dpr);
        const ratio = Math.min(cssW / img.width, cssH / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.drawImage(img, 0, 0, w, h);
        diagramOnCanvasRef.current = true;
        // Do NOT call emitChange — the bare diagram is not an answer yet;
        // emitChange fires on the student's first stroke via handlePointerUp.
      };
      img.src = safeDataUrl;
    };

    if (primaryUrl.startsWith('data:')) {
      place(primaryUrl);
      return;
    }
    const tmp = new Image();
    tmp.crossOrigin = 'anonymous';
    tmp.onload = () => {
      const c = document.createElement('canvas');
      c.width = tmp.naturalWidth;
      c.height = tmp.naturalHeight;
      c.getContext('2d').drawImage(tmp, 0, 0);
      try { place(c.toDataURL('image/png')); } catch { place(primaryUrl); }
    };
    tmp.onerror = () => place(primaryUrl);
    tmp.src = primaryUrl;
  }, [questionIdx, autoLoadQuestionDiagram, initialDrawing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Set canvas physical size once on mount + when canvasHeight changes ----
  // NO ResizeObserver — it was firing during drawing (async callbacks slip between
  // pointer events), causing content shift, progressive shrinkage, and undo breakage.
  // Container width is stable during an exam so we only need to measure it once.
  const lastDimsRef = useRef({ w: 0, h: 0 });

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cssW = Math.round(rect.width) || 800;
    const cssH = canvasHeight;
    const newW = Math.round(cssW * dpr);
    const newH = Math.round(cssH * dpr);

    // Skip if dimensions haven't actually changed
    if (newW === lastDimsRef.current.w && newH === lastDimsRef.current.h) return;

    // Preserve current drawing before resizing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

    canvas.width = newW;
    canvas.height = newH;
    lastDimsRef.current = { w: newW, h: newH };
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Restore drawing scaled to new size (only meaningful after resize-handle drag)
    if (tempCanvas.width > 0 && tempCanvas.height > 0) {
      ctx.drawImage(tempCanvas, 0, 0, cssW, cssH);
    }
  }, [canvasHeight]);

  // ---- Annotation screenshot → floating image (runs after canvas sizing) ----
  useEffect(() => {
    if (!pendingFloatImage) return;
    handleCapturedImage(pendingFloatImage);
    onFloatImageConsumed?.();
  }, [pendingFloatImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Auto-commit: when autoInsertFloating=true, commit the floating image immediately ----
  // floatingImage state is set asynchronously inside handleCapturedImage (after Image loads),
  // so we watch it here rather than trying to schedule inside handleCapturedImage.
  const autoInsertFloatingRef = useRef(autoInsertFloating);
  autoInsertFloatingRef.current = autoInsertFloating;
  const commitFloatingImageRef = useRef(null); // populated below after commitFloatingImage is defined

  useEffect(() => {
    if (!autoInsertFloatingRef.current) return;
    if (!floatingImage) return;
    // Defer one frame so the floating image renders at its correct position/size before commit
    const raf = requestAnimationFrame(() => {
      commitFloatingImageRef.current?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [floatingImage]);

  // ---- Emit strokes-only JPEG whenever a stroke is committed ----
  // Context compositing (question chart) happens at verify time, not here,
  // so the stored answer is strokes-only and won't re-render the question
  // image inside the drawing pad when the student revisits the question.
  // When autoLoadQuestionDiagram baked a diagram onto the canvas, we pass
  // { dataUrl, diagramOnCanvas: true } so the caller can skip compositeWithContext.
  const emitChange = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    {
      const ctx = canvas.getContext('2d');
      let isBlank = false;
      try {
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        isBlank = data.every((v) => v === 0);
      } catch {
        // Canvas tainted by cross-origin image — skip blank check, emit as-is
      }
      if (isBlank) { onChange(null); return; }
    }

    const tmp = document.createElement('canvas');
    tmp.width = canvas.width;
    tmp.height = canvas.height;
    const tctx = tmp.getContext('2d');
    tctx.fillStyle = '#ffffff';
    tctx.fillRect(0, 0, canvas.width, canvas.height);
    tctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
    ownEmitRef.current = true; // prevent useEffect from wiping history when initialDrawing prop updates
    const dataUrl = tmp.toDataURL('image/jpeg', 0.92);
    if (diagramOnCanvasRef.current) {
      onChange({ dataUrl, diagramOnCanvas: true });
    } else {
      onChange(dataUrl);
    }
  }, [onChange]);

  // ---- Canvas pointer helpers ----
  // Coordinates are in CSS pixels (the ctx is scaled by DPR so we don't need to correct here)
  const relPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  // Returns ImageData for shape-tool preview (putImageData during drag)
  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }, []);

  // Returns a data URL for undo history — dimension-independent so it survives canvas resizes
  const saveUndoSnapshot = useCallback(() => {
    return canvasRef.current.toDataURL('image/png');
  }, []);

  // ---- Lock/unlock nearest scrollable ancestor to prevent page shift while drawing ----
  const scrollLockRef = useRef(null);
  const lockParentScroll = useCallback(() => {
    let el = canvasRef.current?.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      if (style.overflow === 'auto' || style.overflow === 'scroll' ||
          style.overflowY === 'auto' || style.overflowY === 'scroll') {
        const lockedEl = el;
        scrollLockRef.current = { el: lockedEl, top: lockedEl.scrollTop, left: lockedEl.scrollLeft };
        // Freeze scroll position — any attempt to scroll is immediately reverted
        lockedEl._drawLockHandler = () => {
          lockedEl.scrollTop = scrollLockRef.current.top;
          lockedEl.scrollLeft = scrollLockRef.current.left;
        };
        lockedEl.addEventListener('scroll', lockedEl._drawLockHandler);
        break;
      }
      el = el.parentElement;
    }
  }, []);

  const unlockParentScroll = useCallback(() => {
    const lock = scrollLockRef.current;
    if (lock?.el?._drawLockHandler) {
      lock.el.removeEventListener('scroll', lock.el._drawLockHandler);
      delete lock.el._drawLockHandler;
    }
    scrollLockRef.current = null;
  }, []);

  const handlePointerDown = useCallback(
    (e) => {
      e.preventDefault();
      // Text tool: place cursor at click position instead of drawing
      if (activeTool === TOOL.TEXT) {
        const { x, y } = relPos(e);
        setTextCursor({ x, y, text: '' });
        return;
      }
      canvasRef.current.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      lockParentScroll();
      const { x, y } = relPos(e);

      // Save state before stroke — ImageData for shape preview, data URL for undo
      snapshotRef.current = saveSnapshot();
      undoSnapshotRef.current = saveUndoSnapshot();

      currentStrokeRef.current = { startX: x, startY: y, lastX: x, lastY: y };

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      if (activeTool === TOOL.PEN) {
        ctx.beginPath();
        ctx.moveTo(x, y);
      }
    },
    [activeTool, relPos, saveSnapshot, saveUndoSnapshot, lockParentScroll]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!drawingRef.current || !currentStrokeRef.current) return;
      e.preventDefault();
      const { x, y } = relPos(e);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const { startX, startY } = currentStrokeRef.current;
      const width = activeTool === TOOL.ERASER ? ERASER_WIDTHS[strokeSize] : STROKE_WIDTHS[strokeSize];

      if (activeTool === TOOL.PEN) {
        ctx.strokeStyle = drawColor;
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineTo(x, y);
        ctx.stroke();
      } else if (activeTool === TOOL.ERASER) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, width / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      } else {
        // Shape tools: restore snapshot each frame then draw preview
        ctx.putImageData(snapshotRef.current, 0, 0);
        ctx.strokeStyle = drawColor;
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();

        if (activeTool === TOOL.LINE) {
          ctx.moveTo(startX, startY);
          ctx.lineTo(x, y);
          ctx.stroke();
        } else if (activeTool === TOOL.RECT) {
          ctx.strokeRect(startX, startY, x - startX, y - startY);
        } else if (activeTool === TOOL.CIRCLE) {
          const cx = (startX + x) / 2;
          const cy = (startY + y) / 2;
          const rx = Math.abs(x - startX) / 2;
          const ry = Math.abs(y - startY) / 2;
          ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      currentStrokeRef.current = { ...currentStrokeRef.current, lastX: x, lastY: y };
    },
    [activeTool, drawColor, strokeSize, relPos]
  );

  const handlePointerUp = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    unlockParentScroll();

    if (activeTool === TOOL.PEN) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.closePath();
    }

    // Commit data-URL snapshot to undo history only if the stroke moved (not just a tap)
    const s = currentStrokeRef.current;
    const moved = s && (Math.abs((s.lastX ?? s.startX) - s.startX) > 2 || Math.abs((s.lastY ?? s.startY) - s.startY) > 2);
    if (undoSnapshotRef.current && (moved || activeTool !== TOOL.PEN)) {
      setHistory((prev) => [...prev, undoSnapshotRef.current]);
      setCanUndo(true);
      undoSnapshotRef.current = null;
    } else {
      undoSnapshotRef.current = null;
    }
    snapshotRef.current = null;

    currentStrokeRef.current = null;
    userStrokesRef.current = true;
    ownEmitRef.current = true;
    emitChange();
  }, [activeTool, emitChange, unlockParentScroll]);

  // ---- Undo (restores from dimension-independent data URL) ----
  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const prevDataUrl = history[history.length - 1];
    const newHistory = history.slice(0, -1);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const img = new Image();
    img.onload = () => {
      // Reset transform → draw snapshot at full physical resolution → restore DPR scale
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ownEmitRef.current = true;
      emitChange();
    };
    img.src = prevDataUrl;
    if (newHistory.length === 0) userStrokesRef.current = false;
    setHistory(newHistory);
    setCanUndo(newHistory.length > 0);
  }, [history, emitChange]);

  // ---- Clear ----
  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    userStrokesRef.current = false;
    setCanUndo(true);
    if (onChange) onChange(null);
  }, [onChange]);

  // ---- Insert captured screenshot as a resizable/draggable floating overlay ----
  // Always converts external URLs to data URLs first so the canvas is never tainted.
  const handleCapturedImage = useCallback((src) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const containerRect = container?.getBoundingClientRect();
    const cssW = (containerRect?.width > 0 ? containerRect.width : canvas.width / dpr) || 800;
    const cssH = (canvas.height > 0 ? canvas.height / dpr : containerRect?.height) || 350;

    const place = (safeDataUrl) => {
      const img = new Image();
      img.onload = () => {
        // AreaCaptureOverlay captures at devicePixelRatio scale and outputs as JPEG.
        // Dividing by DPR converts physical pixels → CSS pixels so the floating image
        // displays at the original selection size with a crisp 1:1 pixel mapping.
        // External/question images (PNG paths) are already at CSS pixel dimensions.
        const captureDpr = safeDataUrl.startsWith('data:image/jpeg') ? (window.devicePixelRatio || 1) : 1;
        const imgCssW = Math.round(img.width / captureDpr);
        const imgCssH = Math.round(img.height / captureDpr);
        // baseW/baseH always equal true native CSS size so that 100% = pixel-perfect quality.
        // Fit image to full canvas width; if it's already narrower, show at 1:1.
        const ratio = Math.min(cssW / imgCssW, 1);
        const w = Math.round(imgCssW * ratio);
        const h = Math.round(imgCssH * ratio);
        // Auto-expand canvas height to fit the image (+ 40px breathing room).
        if (h + 40 > cssH) {
          setCanvasHeight(h + 40);
        }
        setFloatingImage({ src: safeDataUrl, x: 0, y: 0, w, h, baseW: imgCssW, baseH: imgCssH });
        setLastCapturedSrc(safeDataUrl);
      };
      img.src = safeDataUrl;
    };

    // Already a data URL — safe to use directly
    if (src.startsWith('data:')) { place(src); return; }

    // External URL — convert to data URL via an offscreen canvas (with crossOrigin)
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const tmp = document.createElement('canvas');
      tmp.width = img.naturalWidth;
      tmp.height = img.naturalHeight;
      tmp.getContext('2d').drawImage(img, 0, 0);
      try {
        place(tmp.toDataURL('image/png'));
      } catch {
        // CORS blocked conversion — use the raw URL but guard emitChange
        place(src);
      }
    };
    img.onerror = () => place(src);
    img.src = src;
  }, []);

  // ---- Upload a photo / image from device (tablet camera or gallery) ----
  const photoInputRef = useRef(null);
  const handlePhotoUpload = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') handleCapturedImage(reader.result);
    };
    reader.readAsDataURL(file);
  }, [handleCapturedImage]);

  // Commit floating image onto the canvas at its current position/size
  const commitFloatingImage = useCallback(() => {
    if (!floatingImage) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
    setCanUndo(true);
    const { src, x, y, w, h } = floatingImage;
    setFloatingImage(null);
    const img = new Image();
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.drawImage(img, x, y, w, h);
      userStrokesRef.current = true;
      ownEmitRef.current = true;
      emitChange();
    };
    img.src = src;
  }, [floatingImage, emitChange]);
  commitFloatingImageRef.current = commitFloatingImage;

  const commitTextCursor = useCallback(() => {
    if (!textCursor || !textCursor.text.trim()) { setTextCursor(null); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
    setCanUndo(true);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = `${fontSize}px sans-serif`;
    ctx.fillStyle = drawColor;
    const lineH = fontSize * 1.35;
    textCursor.text.split('\n').forEach((line, i) => {
      ctx.fillText(line, textCursor.x, textCursor.y + i * lineH);
    });
    userStrokesRef.current = true;
    ownEmitRef.current = true;
    emitChange();
    setTextCursor(null);
  }, [textCursor, fontSize, drawColor, emitChange]);

  const setFloatScale = useCallback((pct) => {
    const clamped = Math.max(FLOAT_SCALE_MIN, Math.min(FLOAT_SCALE_MAX, pct));
    setFloatingImage((prev) => {
      if (!prev?.baseW) return prev;
      const w = Math.round(prev.baseW * clamped / 100);
      const h = Math.round(prev.baseH * clamped / 100);
      return { ...prev, w, h };
    });
  }, []);

  const floatScalePct = floatingImage?.baseW
    ? Math.round((floatingImage.w / floatingImage.baseW) * 100)
    : 100;

  const handleFloatPointerDown = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const { w: startW, h: startH, x: startImgX, y: startImgY } = floatingImage;
    const aspect = startH / startW;

    const onMove = (me) => {
      const dx = me.clientX - startX;
      const dy = me.clientY - startY;
      if (type === 'move') {
        setFloatingImage((prev) => prev ? ({ ...prev, x: startImgX + dx, y: startImgY + dy }) : prev);
        return;
      }
      let newW = startW;
      let newX = startImgX;
      let newY = startImgY;
      if (type === 'resize-br') {
        newW = Math.max(FLOAT_MIN_SIZE, startW + dx);
      } else if (type === 'resize-bl') {
        newW = Math.max(FLOAT_MIN_SIZE, startW - dx);
        newX = startImgX + (startW - newW);
      } else if (type === 'resize-tr') {
        newW = Math.max(FLOAT_MIN_SIZE, startW + dx);
        newY = startImgY + (startH - Math.round(newW * aspect));
      } else if (type === 'resize-tl') {
        newW = Math.max(FLOAT_MIN_SIZE, startW - dx);
        newX = startImgX + (startW - newW);
        newY = startImgY + (startH - Math.round(newW * aspect));
      }
      const newH = Math.round(newW * aspect);
      setFloatingImage((prev) => prev ? ({ ...prev, x: newX, y: newY, w: newW, h: newH }) : prev);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [floatingImage]);

  // ---- Prevent page scroll while drawing (touch devices) ----
  // Pointer events + CSS touchAction: 'none' don't reliably prevent scroll on all
  // browsers/devices. Explicit touch listeners with { passive: false } are required
  // so that preventDefault() actually cancels the native scroll gesture.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const preventTouchStart = (e) => {
      // Always prevent default on touchstart within the canvas area to stop the
      // browser from initiating a scroll gesture before pointerdown fires.
      e.preventDefault();
    };
    const preventTouchMove = (e) => {
      e.preventDefault();
    };
    // Block wheel events on the canvas so mouse-wheel doesn't scroll the parent
    const preventWheel = (e) => { e.preventDefault(); };
    canvas.addEventListener('touchstart', preventTouchStart, { passive: false });
    canvas.addEventListener('touchmove', preventTouchMove, { passive: false });
    canvas.addEventListener('wheel', preventWheel, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', preventTouchStart);
      canvas.removeEventListener('touchmove', preventTouchMove);
      canvas.removeEventListener('wheel', preventWheel);
    };
  }, []);

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo]);

  // ---- Drag-and-drop image onto canvas ----
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);

    // Priority 1: file dragged from OS (image file)
    const file = Array.from(e.dataTransfer.files || []).find((f) => f.type.startsWith('image/'));
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => handleCapturedImage(ev.target.result);
      reader.readAsDataURL(file);
      return;
    }

    // Priority 2: image dragged from browser — try to get the src URL
    let src = null;
    const html = e.dataTransfer.getData('text/html');
    if (html) {
      const match = html.match(/src=["']([^"']+)["']/i);
      if (match) src = match[1];
    }
    if (!src) src = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain') || null;

    if (src && /^https?:\/\/|^data:image\//i.test(src)) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const tmp = document.createElement('canvas');
        tmp.width = img.naturalWidth;
        tmp.height = img.naturalHeight;
        tmp.getContext('2d').drawImage(img, 0, 0);
        try {
          handleCapturedImage(tmp.toDataURL('image/png'));
        } catch {
          // CORS tainted — fall back to using the URL directly as the image src
          handleCapturedImage(src);
        }
      };
      img.onerror = () => handleCapturedImage(src);
      img.src = src;
    }
  }, [handleCapturedImage]);

  // ---- Vertical resize handle ----
  const resizeStartRef = useRef(null);
  const handleResizeMouseDown = (e) => {
    resizeStartRef.current = { y: e.clientY, height: canvasHeight };
    const onMove = (me) => {
      const delta = me.clientY - resizeStartRef.current.y;
      setCanvasHeight(Math.max(minHeight, resizeStartRef.current.height + delta));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const toolbarBg = '#f0f4ff';
  const toolbarBorder = '#c5d0f0';

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', border: `1px solid ${toolbarBorder}`, borderRadius: 2, overflow: 'hidden' }}>
      {/* ---- Toolbar ---- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.75,
          py: 0.25,
          bgcolor: toolbarBg,
          borderBottom: `1px solid ${toolbarBorder}`,
          flexWrap: 'wrap',
          rowGap: 0.25,
          ...(floatingImage && { opacity: 0.4, pointerEvents: 'none' }),
        }}
      >
        {label && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5 }}>
              {label}
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

        {/* Drawing Tools */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={activeTool}
          onChange={(_, v) => v && setActiveTool(v)}
          sx={{ '& .MuiToggleButton-root': { px: 0.5, py: 0.25, minWidth: 0 } }}
        >
          <Tooltip title="Pen — freehand writing">
            <ToggleButton value={TOOL.PEN}>
              <Brush fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Eraser">
            <ToggleButton value={TOOL.ERASER}>
              <AutoFixNormal fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Straight line">
            <ToggleButton value={TOOL.LINE}>
              <Remove fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Rectangle">
            <ToggleButton value={TOOL.RECT}>
              <CropSquare fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Circle / Ellipse">
            <ToggleButton value={TOOL.CIRCLE}>
              <RadioButtonUnchecked fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Type text on canvas">
            <ToggleButton value={TOOL.TEXT}>
              <TextIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Stroke Width */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={strokeSize}
          onChange={(_, v) => v && setStrokeSize(v)}
          sx={{ '& .MuiToggleButton-root': { px: 0.5, py: 0.25, minWidth: 0 } }}
        >
          <Tooltip title="Thin stroke">
            <ToggleButton value="S">
              <FiberManualRecord sx={{ fontSize: 8 }} />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Medium stroke">
            <ToggleButton value="M">
              <FiberManualRecord sx={{ fontSize: 13 }} />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Thick stroke">
            <ToggleButton value="L">
              <FiberManualRecord sx={{ fontSize: 18 }} />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Color presets */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {COLOR_PRESETS.map((c) => (
            <Tooltip key={c} title={c === '#000000' ? 'Black' : c === '#1565c0' ? 'Blue' : 'Red'}>
              <Box
                onClick={() => setDrawColor(c)}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  border: drawColor === c ? '2px solid #374151' : '2px solid transparent',
                  boxShadow: drawColor === c ? '0 0 0 2px white inset' : 'none',
                  flexShrink: 0,
                  transition: 'border 0.1s',
                }}
              />
            </Tooltip>
          ))}
          {/* Custom color picker */}
          <Tooltip title="Custom colour">
            <Box
              component="input"
              type="color"
              value={drawColor}
              onChange={(e) => setDrawColor(e.target.value)}
              sx={{
                width: 22,
                height: 22,
                border: '1px solid #ccc',
                borderRadius: 1,
                cursor: 'pointer',
                p: 0,
                bgcolor: 'transparent',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Undo / Clear */}
        <Tooltip title="Undo last stroke (Ctrl+Z)">
          <span>
            <IconButton size="small" onClick={handleUndo} disabled={!canUndo}>
              <Undo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Clear canvas">
          <span>
            <IconButton size="small" onClick={handleClear} disabled={!initialDrawing && history.length === 0 && !canUndo}>
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {activeTool === TOOL.TEXT && (
          <>
            <Tooltip title="Font size">
              <Box component="select"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                sx={{ fontSize: '0.75rem', border: '1px solid #ccc', borderRadius: 1, px: 0.5, py: 0.25, cursor: 'pointer', bgcolor: 'background.paper' }}
              >
                {[12, 16, 20, 24, 28, 32, 40].map((s) => (
                  <option key={s} value={s}>{s}px</option>
                ))}
              </Box>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          </>
        )}

        <Tooltip title="Capture a screen area and insert it as your drawing answer">
          <IconButton size="small" onClick={() => setCaptureMode(true)}>
            <CropIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Upload a photo or image of your working (take a photo on tablet)">
          <IconButton size="small" onClick={() => photoInputRef.current?.click()}>
            <UploadPhotoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handlePhotoUpload}
        />

        {lastCapturedSrc && (
          <Tooltip title="Re-insert last captured image">
            <IconButton size="small" onClick={() => handleCapturedImage(lastCapturedSrc)}>
              <Replay fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {contextImageUrl && (
          <Tooltip title={showDiagram ? 'Hide diagram reference' : 'Show diagram reference'}>
            <IconButton size="small" onClick={() => setShowDiagram((v) => !v)} color={showDiagram ? 'primary' : 'default'}>
              {showDiagram ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {/* Load question image(s) onto canvas */}
        {(() => {
          const base = Array.isArray(questionImages) && questionImages.length > 0 ? questionImages : [];
          const hasSnapshot = !!contextImageUrl;
          const snapshotEntry = hasSnapshot ? [{ label: base.length === 0 ? 'Question diagram' : 'Full question view', url: contextImageUrl }] : [];
          const imgs = [...base, ...snapshotEntry];
          if (imgs.length === 0) return null;
          if (imgs.length === 1) {
            return (
              <Tooltip title={`Load ${imgs[0].label} onto canvas`}>
                <IconButton size="small" onClick={() => handleCapturedImage(imgs[0].url)}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          }
          return (
            <>
              <Tooltip title="Load a question image onto canvas">
                <IconButton size="small" onClick={(e) => setImgMenuAnchor(e.currentTarget)}>
                  <PermMedia fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={imgMenuAnchor} open={Boolean(imgMenuAnchor)} onClose={() => setImgMenuAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 280, minWidth: 200 } } }}>
                {imgs.map((img, i) => (
                  <MenuItem key={i} onClick={() => { handleCapturedImage(img.url); setImgMenuAnchor(null); }} dense>
                    <ListItemIcon sx={{ minWidth: 30 }}><ImageIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={img.label} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          );
        })()}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

        {/* Background mode: lines / grid / dots */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={bgModeState}
          onChange={(_, v) => v && setBgModeState(v)}
          sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.5 } }}
        >
          <Tooltip title="Lined paper">
            <ToggleButton value="lines">
              <ViewAgenda fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Grid paper">
            <ToggleButton value="grid">
              <GridOn fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Dot grid">
            <ToggleButton value="dots">
              <MoreHoriz fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      {/* ---- Question diagram reference — shown only when toggled on ---- */}
      {contextImageUrl && showDiagram && (
        <Box sx={{ bgcolor: '#f9f9f9', border: '1px solid #e0e0e0', borderBottom: 'none', px: 1, pt: 1, pb: 0.5 }}>
          <Box sx={{ fontSize: '0.68rem', color: 'text.secondary', fontStyle: 'italic', mb: 0.5 }}>
            Question diagram (for reference)
          </Box>
          <Box
            component="img"
            src={contextImageUrl}
            alt="Question diagram"
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{ display: 'block', maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 1 }}
          />
        </Box>
      )}

      {/* ---- Canvas ---- */}
      <Box ref={containerRef} sx={{ position: 'relative', bgcolor: '#ffffff', lineHeight: 0, touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: canvasHeight,
            cursor:
              activeTool === TOOL.ERASER
                ? 'cell'
                : activeTool === TOOL.PEN
                ? 'crosshair'
                : 'crosshair',
            touchAction: 'none',
            userSelect: 'none',
            // Yield all pointer events to the floating image while it is visible
            pointerEvents: floatingImage ? 'none' : 'auto',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        />
        {dragOver && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 40,
            border: '3px dashed #1976d2', borderRadius: 1,
            bgcolor: 'rgba(25,118,210,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <Box sx={{ bgcolor: 'rgba(25,118,210,0.85)', color: 'white', px: 2, py: 1, borderRadius: 2, fontWeight: 700, fontSize: '0.9rem' }}>
              Drop image here
            </Box>
          </Box>
        )}

        {/* Hint overlay — shown while a floating image is pending insertion.
            Sits above the canvas (zIndex 9) but below the floating image (zIndex 10). */}
        {floatingImage && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 9,
            bgcolor: 'rgba(240,244,255,0.72)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            pb: 1.5,
            pointerEvents: 'none',
          }}>
            <Box sx={{
              bgcolor: 'rgba(25,118,210,0.88)',
              color: 'white',
              px: 2, py: 0.75,
              borderRadius: 2,
              fontSize: '0.78rem',
              fontWeight: 600,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}>
              Click <strong>Insert ✓</strong> to place the image, then use drawing tools
            </Box>
          </Box>
        )}


        {/* Text input overlay — Photoshop-style click-to-type on canvas */}
        {textCursor && (
          <Box
            component="textarea"
            autoFocus
            value={textCursor.text}
            onChange={(e) => setTextCursor((prev) => ({ ...prev, text: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitTextCursor(); }
              if (e.key === 'Escape') setTextCursor(null);
            }}
            onBlur={commitTextCursor}
            sx={{
              position: 'absolute',
              left: textCursor.x,
              top: textCursor.y - fontSize,
              minWidth: 120,
              background: 'transparent',
              border: '1px dashed #1976d2',
              outline: 'none',
              resize: 'both',
              fontFamily: 'sans-serif',
              fontSize: `${fontSize}px`,
              color: drawColor,
              lineHeight: 1.35,
              zIndex: 30,
              padding: '2px 4px',
              overflow: 'hidden',
            }}
            rows={1}
            placeholder="Type here…"
          />
        )}

        {/* Floating captured image — draggable/resizable before committing to canvas.
            Outer Box handles 'move' drag; canvas pointer-events disabled while active. */}
        {floatingImage && (
          <Box
            onPointerDown={(e) => handleFloatPointerDown(e, 'move')}
            sx={{
              position: 'absolute',
              left: floatingImage.x,
              top: floatingImage.y,
              zIndex: 10,
              userSelect: 'none',
              touchAction: 'none',
              cursor: 'move',
            }}
          >
            {/* Image (pointer-events none — drag handled by outer Box) */}
            <Box
              component="img"
              src={floatingImage.src}
              draggable={false}
              sx={{
                display: 'block',
                width: floatingImage.w,
                height: floatingImage.h,
                border: '2px dashed #1976d2',
                boxSizing: 'border-box',
                pointerEvents: 'none',
              }}
            />

            {/* Scale + action toolbar — inside image top, stops move-drag propagation */}
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                bgcolor: 'rgba(255,255,255,0.95)',
                borderBottom: '1px solid #c5d0f0',
                px: 0.75,
                py: 0.5,
                zIndex: 30,
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Box
                component="button"
                onClick={commitFloatingImage}
                sx={{ px: 1.5, py: 0.25, bgcolor: '#1976d2', color: 'white', border: 'none', borderRadius: 1, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: '#1565c0' } }}
              >
                Insert ✓
              </Box>
              <Box
                component="button"
                onClick={() => setFloatingImage(null)}
                sx={{ px: 1.5, py: 0.25, bgcolor: '#e0e0e0', color: '#333', border: 'none', borderRadius: 1, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: '#bdbdbd' } }}
              >
                ✕
              </Box>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
              <Box
                component="input"
                type="number"
                min={FLOAT_SCALE_MIN}
                max={FLOAT_SCALE_MAX}
                value={floatScalePct}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) setFloatScale(v);
                }}
                sx={{ width: 48, fontSize: '0.72rem', border: '1px solid #ccc', borderRadius: 0.5, px: 0.5, py: 0.25, textAlign: 'center' }}
              />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', flexShrink: 0 }}>%</Typography>
            </Box>

            {/* 4 corner resize handles — inset to stay within overflow:hidden boundary */}
            {[
              { type: 'resize-tl', sx: { left: 2, top: 40, cursor: 'nw-resize' } },
              { type: 'resize-tr', sx: { right: 2, top: 40, cursor: 'ne-resize' } },
              { type: 'resize-bl', sx: { left: 2, bottom: 2, cursor: 'sw-resize' } },
              { type: 'resize-br', sx: { right: 2, bottom: 2, cursor: 'se-resize' } },
            ].map(({ type, sx }) => (
              <Box
                key={type}
                onPointerDown={(e) => { e.stopPropagation(); handleFloatPointerDown(e, type); }}
                sx={{
                  position: 'absolute',
                  width: 16,
                  height: 16,
                  bgcolor: '#1976d2',
                  border: '2px solid white',
                  borderRadius: '50%',
                  zIndex: 26,
                  ...sx,
                }}
              />
            ))}
          </Box>
        )}

        {/* Faint background guide: lines / grid / dots */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: canvasHeight,
            pointerEvents: 'none',
            opacity: 0.12,
          }}
          preserveAspectRatio="none"
        >
          {bgModeState === 'dots' ? (
            Array.from({ length: Math.ceil(canvasHeight / 32) }, (_, row) =>
              Array.from({ length: 50 }, (_, col) => (
                <circle
                  key={`${row}-${col}`}
                  cx={col * 32}
                  cy={32 + row * 32}
                  r={1}
                  fill="#3f51b5"
                />
              ))
            ).flat()
          ) : (
            <>
              {Array.from({ length: Math.ceil(canvasHeight / 32) }, (_, i) => (
                <line key={`h-${i}`} x1="0" y1={32 + i * 32} x2="100%" y2={32 + i * 32} stroke="#3f51b5" strokeWidth="1" />
              ))}
              {bgModeState === 'grid' && Array.from({ length: 50 }, (_, i) => (
                <line key={`v-${i}`} x1={i * 32} y1="0" x2={i * 32} y2="100%" stroke="#3f51b5" strokeWidth="1" />
              ))}
            </>
          )}
        </svg>
      </Box>

      {/* ---- Resize handle ---- */}
      <Box
        onMouseDown={handleResizeMouseDown}
        sx={{
          height: 8,
          bgcolor: toolbarBg,
          borderTop: `1px solid ${toolbarBorder}`,
          cursor: 'ns-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: 32, height: 3, borderRadius: 2, bgcolor: '#b0bec5' }} />
      </Box>

    </Box>

    {captureMode && (
      <AreaCaptureOverlay
        onCapture={(dataUrl) => { handleCapturedImage(dataUrl); setCaptureMode(false); }}
        onCancel={() => setCaptureMode(false)}
      />
    )}
  </>
  );
}
